/**
 * Every supplier quotation in one list, so a job can be priced up without
 * opening each lead in turn. Quotations on a confirmed lead can be ordered
 * from; the rest are still being worked.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Download, Search } from 'lucide-react';
import { api } from '../../lib/api';
import { peso, shortDate } from '../../lib/format';
import {
  Card,
  EmptyState,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  inputClass,
} from '../../components/ui';
import { downloadQuotation } from './QuotationsCard';

interface Row {
  id: string;
  quoteRef: string | null;
  quoteDate: string;
  validUntil: string | null;
  description: string | null;
  amount: string;
  vatIncluded: boolean;
  status: 'RECEIVED' | 'SELECTED' | 'DECLINED';
  supplier: { id: string; code: string; name: string };
  file: { id: string; originalName: string } | null;
  lead: {
    id: string;
    code: string;
    projectName: string;
    customer: { id: string; name: string };
    salesOrders: { id: string; code: string; projectName: string }[];
  };
  _count: { purchaseOrders: number };
}

const STATUS_TONE = {
  RECEIVED: 'neutral',
  SELECTED: 'ok',
  DECLINED: 'warning',
} as const;

export function QuotationsPage(): JSX.Element {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [confirmedOnly, setConfirmedOnly] = useState(false);

  const params = new URLSearchParams({ pageSize: '100' });
  if (search) params.set('q', search);
  if (status) params.set('status', status);
  if (confirmedOnly) params.set('confirmedOnly', 'true');

  const quotations = useQuery({
    queryKey: ['quotations', 'list', params.toString()],
    queryFn: () => api<{ data: Row[]; total: number }>(`/quotations?${params.toString()}`),
  });

  const rows = quotations.data?.data ?? [];

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <header>
        <h1 className="text-xl font-bold text-navy dark:text-white">Supplier quotations</h1>
        <p className="text-sm text-slate-500">
          Every price a supplier has given us, across all leads. Open the sales order to raise a
          purchase order against one.
        </p>
      </header>

      <Card>
        <div className="flex flex-wrap gap-3">
          <label className="relative min-w-[16rem] flex-1">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            />
            <input
              className={`${inputClass} pl-9`}
              placeholder="Search supplier, reference, lead or job"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label="Search quotations"
            />
          </label>
          <select
            className={`${inputClass} w-44`}
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            aria-label="Filter by status"
          >
            <option value="">Any status</option>
            <option value="RECEIVED">Received</option>
            <option value="SELECTED">Selected</option>
            <option value="DECLINED">Declined</option>
          </select>
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm">
          <input
            className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand"
            type="checkbox"
            checked={confirmedOnly}
            onChange={(event) => setConfirmedOnly(event.target.checked)}
          />
          Only jobs that are confirmed, and can be ordered from
        </label>
      </Card>

      {quotations.isLoading && <Skeleton className="h-64 w-full" />}

      {!quotations.isLoading && rows.length === 0 && (
        <Card>
          <EmptyState
            title="No quotations found"
            description="Quotations are added on the lead, under Supplier quotations."
          />
        </Card>
      )}

      {rows.length > 0 && (
        <Card>
          <TableWrap>
            <thead>
              <tr>
                <Th>Supplier</Th>
                <Th>For</Th>
                <Th>Reference</Th>
                <Th>Quoted</Th>
                <Th align="right">Amount</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((row) => {
                const order = row.lead.salesOrders[0];
                return (
                  <tr key={row.id}>
                    <Td>
                      <p className="font-semibold">{row.supplier.name}</p>
                      {row.description && (
                        <p className="text-xs text-slate-500">{row.description}</p>
                      )}
                    </Td>
                    <Td>
                      <p>{row.lead.projectName}</p>
                      <p className="text-xs text-slate-500">{row.lead.customer.name}</p>
                      {order ? (
                        <Link
                          to={`/sales-orders/${order.id}`}
                          className="font-mono text-xs text-brand hover:underline"
                        >
                          {order.code}
                        </Link>
                      ) : (
                        <Link
                          to={`/leads/${row.lead.id}`}
                          className="font-mono text-xs text-slate-500 hover:underline"
                        >
                          {row.lead.code} · not yet confirmed
                        </Link>
                      )}
                    </Td>
                    <Td>
                      <p className="text-xs">{row.quoteRef ?? '—'}</p>
                      {row.file && (
                        <button
                          type="button"
                          className="mt-0.5 inline-flex items-center gap-1 text-xs text-brand hover:underline"
                          onClick={() => downloadQuotation(row.id, row.file!.originalName)}
                        >
                          <Download aria-hidden="true" className="h-3 w-3" /> file
                        </button>
                      )}
                    </Td>
                    <Td>
                      <p className="text-xs">{shortDate(row.quoteDate)}</p>
                      {row.validUntil && (
                        <p className="text-xs text-slate-500">until {shortDate(row.validUntil)}</p>
                      )}
                    </Td>
                    <Td align="right">
                      {peso(row.amount)}
                      <span className="block text-xs text-slate-500">
                        {row.vatIncluded ? 'VAT included' : 'plus VAT'}
                      </span>
                    </Td>
                    <Td>
                      <StatusChip tone={STATUS_TONE[row.status]}>
                        {row.status.charAt(0) + row.status.slice(1).toLowerCase()}
                      </StatusChip>
                      {row._count.purchaseOrders > 0 && (
                        <span className="mt-1 block text-xs text-slate-500">
                          {row._count.purchaseOrders} PO
                          {row._count.purchaseOrders === 1 ? '' : 's'}
                        </span>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </TableWrap>
        </Card>
      )}
    </div>
  );
}
